/**
 * ImageEditTab — unified "Image Edit" surface.
 *
 * Model-first UX: the user picks a model, then the available operations and
 * fields follow from it.
 *
 *   - Fooocus (local, free on desktop) exposes the full classic operation set
 *     (img2img, face swap, inpaint, outpaint, upscale & vary, enhance,
 *     remove background, describe). Each operation renders its existing,
 *     working form (reused — RULE #0 / RULE #-2).
 *   - Cloud AI editors (qwen-edit-2511, nano-banana, ...) are instruction-based
 *     (one image + one text instruction). The SAME operation row is shown, but
 *     clicking an operation seeds an editable starter instruction instead of
 *     switching forms. Operations that don't fit a pure instruction (outpaint,
 *     upscale, describe) are disabled with a "best with Fooocus" hint.
 *
 * This consolidates the formerly-scattered Image / Edit / Enhance / Creative
 * sub-tabs into one place.
 */

import React, { useMemo, useState } from 'react';
import { Coins, Award, Zap, Loader2, RefreshCw } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { FreeOnDesktopBadge } from '@/components/common';
import { useImageProviderAvailability } from '@/hooks/useImageProviderAvailability';
import { isLocalPricingActive } from '@/utils/localPricing';
import type { ImageEditProvider } from '@/services/imageEditing';
import {
  useImageEditModels,
  FOOOCUS_OPERATIONS,
  CLOUD_OPERATION_PRESETS,
  DEFAULT_IMAGE_EDIT_MODEL_ID,
  getImageEditModel,
  type FooocusOperationId,
  type ImageEditModelOption,
} from '../imageEditModels';
import { ImagePromptTab } from './ImagePromptTab';
import { FaceSwapTab } from './FaceSwapTab';
import { InpaintTab } from './InpaintTab';
import { OutpaintTab } from './OutpaintTab';
import { UpscaleVaryTab } from './UpscaleVaryTab';
import { ImageEnhanceTab } from './ImageEnhanceTab';
import { BackgroundRemovalTab } from './BackgroundRemovalTab';
import { DescribeTab } from './DescribeTab';
import { AIEditTab } from './AIEditTab';

interface ImageEditTabProps {
  /** Forwarded to the Describe operation: "use this prompt" jumps to generate. */
  onUsePrompt: (prompt: string) => void;
}

/** Render the active Fooocus operation's existing form. */
const FooocusOperationBody: React.FC<{
  operation: FooocusOperationId;
  onUsePrompt: (prompt: string) => void;
}> = ({ operation, onUsePrompt }) => {
  switch (operation) {
    case 'image-prompt':
      return <ImagePromptTab />;
    case 'face-swap':
      return <FaceSwapTab />;
    case 'inpaint':
      return <InpaintTab />;
    case 'outpaint':
      return <OutpaintTab />;
    case 'upscale-vary':
      return <UpscaleVaryTab />;
    case 'enhance':
      return <ImageEnhanceTab />;
    case 'remove-background':
      return <BackgroundRemovalTab />;
    case 'describe':
      return <DescribeTab onUsePrompt={onUsePrompt} />;
    default:
      return null;
  }
};

export const ImageEditTab: React.FC<ImageEditTabProps> = ({ onUsePrompt }) => {
  const { t } = useTranslation();

  const [modelId, setModelId] = useState<string>(DEFAULT_IMAGE_EDIT_MODEL_ID);
  const [operation, setOperation] = useState<FooocusOperationId>('image-prompt');
  // Bumped on each cloud operation-chip click so AIEditTab re-applies the template.
  const [presetNonce, setPresetNonce] = useState(0);

  // Fooocus is local and always present; the cloud editors, their names and
  // their prices arrive from GET /api/v1/image-editing/providers. Nothing in
  // this component knows a price -- see imageEditModels.ts for why.
  const {
    models,
    localModels,
    cloudModels,
    isLoading: isCatalogLoading,
    error: catalogError,
    refresh: refreshCatalog,
  } = useImageEditModels();

  const selectedModel = useMemo(() => getImageEditModel(models, modelId), [models, modelId]);
  const isFooocus = selectedModel.kind === 'fooocus';

  // Real, per-model install state — same source of truth as ImageProviderSelector
  // (GET /api/v1/video-providers/image-availability). Fooocus is routing_mode=
  // hybrid: it still generates when not installed locally (routed to
  // fooocus_runpod on RunPod and billed for real), so `isLocalCapable` alone
  // must never drive the "Free" badge — only a confirmed local install does.
  const { availability: imageAvailability } = useImageProviderAvailability();
  const isModelFreeNow = (model: ImageEditModelOption): boolean => {
    if (!model.isLocalCapable) return false;
    const avail = imageAvailability?.get(model.id) ?? null;
    // Conservative while availability hasn't loaded yet: not confirmed free.
    return isLocalPricingActive(avail?.is_local ?? false, avail?.is_installed ?? false);
  };

  // Cloud-editor starter instruction + help line for the active operation.
  const cloudPreset = isFooocus ? undefined : CLOUD_OPERATION_PRESETS[operation];
  const presetText = cloudPreset?.templateKey
    ? t(cloudPreset.templateKey, { defaultValue: cloudPreset.defaultTemplate ?? '' })
    : '';
  const presetHint = cloudPreset?.hintKey
    ? t(cloudPreset.hintKey, { defaultValue: cloudPreset.defaultHint ?? '' })
    : '';

  const handleSelectModel = (id: string) => {
    const nextModel = getImageEditModel(models, id);
    const wasFooocus = isFooocus;
    setModelId(id);
    if (nextModel.kind === 'ai-editor') {
      // A cloud editor can't do every Fooocus operation — fall back to the
      // generic edit when the current one doesn't fit; otherwise keep the
      // user's intent and (when arriving from Fooocus) seed its template.
      const preset = CLOUD_OPERATION_PRESETS[operation];
      if (!preset.supported) {
        setOperation('image-prompt');
      } else if (wasFooocus) {
        setPresetNonce((n) => n + 1);
      }
    }
    // Fooocus: keep the current operation as-is.
  };

  const handleSelectOperation = (opId: FooocusOperationId) => {
    if (isFooocus) {
      setOperation(opId);
      return;
    }
    if (!CLOUD_OPERATION_PRESETS[opId].supported) return; // disabled chip
    setOperation(opId);
    setPresetNonce((n) => n + 1); // seed (or re-seed) the starter instruction
  };

  /**
   * The price cell of a cloud editor's card.
   *
   * `tokensPerEdit` is always set for a model that came back from the catalog,
   * so the "unknown" branch only fires if a future caller renders a cloud model
   * from somewhere else. It says so rather than guessing: this card used to
   * print a constant that had drifted to 30 tokens for an edit billed at 57.
   */
  const renderPrice = (model: ImageEditModelOption) => {
    if (model.tokensPerEdit === undefined) {
      return (
        <span className="inline-flex items-center text-xs text-white/40">
          <Coins className="w-3 h-3 mr-1" />
          {t('image.imageEdit.priceUnknown', { defaultValue: 'price unavailable' })}
        </span>
      );
    }
    const tierHint = model.tierTokensPerEdit
      ? Object.entries(model.tierTokensPerEdit)
          .map(([tier, tokens]) => `${tier}: ${tokens}`)
          .join(' · ')
      : undefined;
    return (
      <span
        className="inline-flex items-center text-xs text-yellow-400"
        title={
          model.pricedBySize
            ? t('image.imageEdit.pricedBySizeHint', {
                defaultValue: 'Price rises with the output size you ask for.',
              }) + (tierHint ? ` (${tierHint})` : '')
            : undefined
        }
      >
        <Coins className="w-3 h-3 mr-1" />
        {model.pricedBySize && (
          <>{t('image.imageEdit.priceFrom', { defaultValue: 'from' })}&nbsp;</>
        )}
        {model.tokensPerEdit}
      </span>
    );
  };

  const renderModelCard = (model: ImageEditModelOption) => {
    const isSelected = model.id === modelId;
    return (
      <button
        key={model.id}
        type="button"
        onClick={() => handleSelectModel(model.id)}
        className={`p-3 rounded-lg border-2 text-left transition-all ${
          isSelected
            ? 'border-primary-500 bg-primary-500/15'
            : 'border-white/10 bg-black/20 hover:border-primary-500/40'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-white">{model.name}</span>
          {model.isLocalCapable ? (
            isModelFreeNow(model) ? (
              <span className="text-[10px] uppercase tracking-wide text-green-400 font-semibold">
                {t('image.imageEdit.freeBadge', { defaultValue: 'Free' })}
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-wide text-white/50 font-semibold">
                {t('image.imageEdit.cloudBadge', { defaultValue: 'Cloud' })}
              </span>
            )
          ) : (
            renderPrice(model)
          )}
        </div>
        <div className="text-xs text-white/50 mt-1">{model.description}</div>
        {!model.isLocalCapable && (
          <div className="flex items-center gap-3 text-[11px] mt-2 text-white/60">
            {model.quality && (
              <span className="inline-flex items-center">
                <Award className="w-3 h-3 mr-1" />
                {model.quality}
              </span>
            )}
            {model.speed && (
              <span className="inline-flex items-center">
                <Zap className="w-3 h-3 mr-1" />
                {model.speed}
              </span>
            )}
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="space-y-6" data-testid="image-edit-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white">
            {t('image.imageEdit.title', { defaultValue: 'Image Edit' })}
          </h3>
          <p className="text-sm text-white/60 mt-1">
            {t('image.imageEdit.subtitle', {
              defaultValue: 'Pick a model, then choose what to do with your image.',
            })}
          </p>
        </div>
        {isFooocus && <FreeOnDesktopBadge />}
      </div>

      {/* Step 1 — Model selector */}
      <div>
        <label className="form-label mb-2">
          {t('image.imageEdit.chooseModel', { defaultValue: 'Choose a model' })}
        </label>

        <p className="text-xs text-white/40 mb-2">
          {t('image.imageEdit.localGroup', { defaultValue: 'Local — free on desktop' })}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          {localModels.map((m) => renderModelCard(m))}
        </div>

        <p className="text-xs text-white/40 mb-2">
          {t('image.imageEdit.cloudGroup', { defaultValue: 'Cloud AI editors' })}
        </p>
        {isCatalogLoading ? (
          <div
            role="status"
            className="flex items-center gap-2 py-4 text-sm text-white/50"
            data-testid="image-edit-catalog-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('image.imageEdit.loadingEditors', {
              defaultValue: 'Loading cloud editors and their prices...',
            })}
          </div>
        ) : catalogError ? (
          // No cached price to fall back on, by design: the previous hardcoded
          // list is exactly how the UI came to quote 30 tokens for a 57-token
          // edit. An editor whose price we cannot fetch is not offered at all
          // (RULE #5) -- Fooocus above is local and unaffected.
          <div
            className="rounded-lg border border-red-500/30 bg-red-500/10 p-3"
            data-testid="image-edit-catalog-error"
          >
            <p className="text-sm text-red-300">
              {t('image.imageEdit.catalogUnavailable', {
                defaultValue:
                  "Cloud editors are unavailable: their prices could not be loaded, and we won't show a price we can't stand behind.",
              })}
            </p>
            <p className="mt-1 break-words text-xs text-red-300/70">{catalogError}</p>
            <button
              type="button"
              onClick={refreshCatalog}
              className="mt-2 inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-red-400/40 px-4 text-sm text-red-200 hover:bg-red-500/10"
            >
              <RefreshCw className="h-4 w-4" />
              {t('common.retry', { defaultValue: 'Try Again' })}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cloudModels.map((m) => renderModelCard(m))}
          </div>
        )}
      </div>

      {/* Step 2 — Operation (shown for every model) */}
      <div>
        <label className="form-label mb-2">
          {t('image.imageEdit.chooseOperation', { defaultValue: 'What do you want to do?' })}
        </label>
        {!isFooocus && (
          <p className="text-xs text-white/40 mb-2">
            {t('image.imageEdit.cloudOperationHint', {
              defaultValue:
                'On cloud editors these fill an editable instruction — no masks or extra uploads.',
            })}
          </p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {FOOOCUS_OPERATIONS.map((op) => {
            const Icon = op.icon;
            const enabled = isFooocus || CLOUD_OPERATION_PRESETS[op.id].supported;
            const isActive = op.id === operation && enabled;
            return (
              <button
                key={op.id}
                type="button"
                disabled={!enabled}
                onClick={() => handleSelectOperation(op.id)}
                title={
                  enabled
                    ? t(op.descKey, { defaultValue: op.defaultDesc })
                    : t('image.imageEdit.bestWithFooocus', { defaultValue: 'Best with Fooocus' })
                }
                className={`relative flex flex-col items-center justify-center gap-1 p-3 rounded-lg border-2 min-h-[72px] transition-all ${
                  isActive
                    ? 'border-primary-500 bg-primary-500/15 text-white'
                    : enabled
                      ? 'border-white/10 bg-black/20 text-white/70 hover:border-primary-500/40'
                      : 'border-white/5 bg-black/10 text-white/30 cursor-not-allowed'
                }`}
              >
                <Icon
                  className={`w-5 h-5 ${
                    isActive ? 'text-primary-400' : enabled ? 'text-white/50' : 'text-white/25'
                  }`}
                />
                <span className="text-xs font-medium text-center leading-tight">
                  {t(op.labelKey, { defaultValue: op.defaultLabel })}
                </span>
                {!enabled && (
                  <span className="absolute top-1 right-1 rounded border border-white/15 px-1 text-[8px] uppercase tracking-wide text-white/40">
                    {t('image.imageEdit.fooocusOnlyBadge', { defaultValue: 'Fooocus' })}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 3 — Operation / editor body */}
      <div className="border-t border-white/10 pt-6">
        {isFooocus ? (
          <FooocusOperationBody
            // Remount the body when switching operations so each form starts fresh.
            key={operation}
            operation={operation}
            onUsePrompt={onUsePrompt}
          />
        ) : (
          // Not keyed by model: switching cloud editors keeps the uploaded
          // image + instruction so the same edit can be retried on another model.
          <AIEditTab
            provider={modelId as ImageEditProvider}
            hideProviderSelector
            instructionPreset={presetText}
            presetNonce={presetNonce}
            operationHint={presetHint}
          />
        )}
      </div>
    </div>
  );
};

export default ImageEditTab;
